<script>
  import { API_BASE_URL } from './config.js';
  import { onMount } from 'svelte';
  import LoadingSpinner from './components/common/LoadingSpinner.svelte';
  import ErrorMessage from './components/common/ErrorMessage.svelte';
  import EmptyState from './components/common/EmptyState.svelte';
  import StatusBadge from './components/common/StatusBadge.svelte';
  import Button from './components/common/Button.svelte';
  import DataTable from './components/tables/DataTable.svelte';
  import ApplicantActionsCell from './components/tables/ApplicantActionsCell.svelte';
  
  export let setPage;
  export const user = null;
  
  let isLoading = false;
  let loadError = null;
  let projects = []; // 회사 사용자가 등록한 프로젝트 목록
  let selectedProject = null; // 선택된 프로젝트
  let applicants = []; // 지원자 목록
  
  // 상태 옵션
  const statusOptions = [
    { id: 'reviewing', label: '검토중' },
    { id: 'proceeding', label: '진행' },
    { id: 'closed', label: '마감' }
  ];

  // 테이블 컬럼 정의
  let applicantColumns = [
    { field: 'name', title: '지원자명', sortable: true },
    { field: 'phone', title: '연락처', sortable: true },
    { field: 'applied_at', title: '지원일자', sortable: true },
    { field: 'actions', title: '상태/액션', sortable: false, width: '250px', component: ApplicantActionsCell }
  ];
  
  // 페이지 로드 시 데이터 가져오기
  onMount(async () => {
    await fetchCompanyProjects();
    
    // 테이블 액션 이벤트 리스너 추가
    const handleStatusChange = (event) => {
      const { applicant, status } = event.detail;
      updateApplicantStatus(applicant, status);
    };
    const handleViewDetail = (event) => viewApplicantDetail(event.detail);
    
    document.addEventListener('statusChange', handleStatusChange);
    document.addEventListener('viewDetail', handleViewDetail);
    
    return () => {
      document.removeEventListener('statusChange', handleStatusChange);
      document.removeEventListener('viewDetail', handleViewDetail);
    };
  });

  // 지원자 데이터를 테이블에 맞게 포맷
  $: formattedApplicants = applicants.map(applicant => ({
    ...applicant,
    name: applicant.name || '이름 없음',
    phone: applicant.phone || '-',
    applied_at: applicant.applied_at ? new Date(applicant.applied_at).toLocaleDateString('ko-KR') : '-',
    actions: { ...applicant, statusOptions } // 액션 컴포넌트에 전달할 데이터
  }));
  
  // 기업 사용자가 등록한 프로젝트 가져오기
  async function fetchCompanyProjects() {
    try {
      isLoading = true;
      loadError = null;
      
      const token = localStorage.getItem('token');
      
      // 기업 사용자 본인이 등록한 프로젝트만 가져오기
      const response = await fetch(`${API_BASE_URL}/api/company/projects`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('프로젝트 목록을 불러오는 데 실패했습니다.');
      }
      
      const data = await response.json();
      projects = data.projects || [];
      
    } catch (error) {
      console.error('프로젝트 로딩 오류:', error);
      loadError = error.message;
    } finally {
      isLoading = false;
    }
  }
  
  // 프로젝트 선택 시 해당 프로젝트의 지원자 목록 가져오기
  async function selectProject(project) {
    try {
      if (!project) return;
      
      isLoading = true;
      loadError = null;
      selectedProject = project;
      
      const token = localStorage.getItem('token');
      
      // 기업 사용자는 자신이 등록한 프로젝트의 지원자만 볼 수 있음
      const response = await fetch(`${API_BASE_URL}/api/company/projects/${project.id}/applicants`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('지원자 목록을 불러오는 데 실패했습니다.');
      }
      
      const data = await response.json();
      applicants = data.applicants || [];
      
    } catch (error) {
      console.error('지원자 목록 로딩 오류:', error);
      loadError = error.message;
    } finally {
      isLoading = false;
    }
  }
  
  // 지원 상태 변경
  async function updateApplicantStatus(applicant, newStatus) {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/company/applications/${applicant.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          status: newStatus
        })
      });
      
      if (!response.ok) {
        throw new Error('지원 상태 변경에 실패했습니다.');
      }
      
      // 성공 시 해당 지원자의 상태 업데이트
      const updatedApplicants = applicants.map(a => 
        a.id === applicant.id ? { ...a, status: newStatus } : a
      );
      
      applicants = updatedApplicants;
      
    } catch (error) {
      console.error('지원 상태 변경 오류:', error);
      alert(`상태 변경 실패: ${error.message}`);
    }
  }
  
  // 지원자 상세 정보 보기
  function viewApplicantDetail(applicant) {
    // 상세 정보 보기 기능 구현
    // (모달 또는 별도 페이지로 구현 가능)
    alert(`${applicant.name} 지원자의 상세 정보 보기`);
  }
  
  // 공고 수정 페이지로 이동
  function editProject() {
    if (selectedProject) {
      // 프로젝트 수정을 위해 localStorage에 선택된 프로젝트 정보를 저장
      localStorage.setItem('editingProject', JSON.stringify(selectedProject));
      // 프로젝트 수정 페이지로 이동
      setPage('project-edit');
    }
  }
</script>

<div class="company-applicants-container">
  <div class="page-header">
    <h2>우리 회사 지원자 관리</h2>
    <div class="header-buttons">
      <button class="back-button" on:click={() => setPage('home')}>
        &lt; 메인으로 돌아가기
      </button>
    </div>
  </div>
  
  {#if isLoading}
    <LoadingSpinner message="데이터를 불러오는 중..." />
  {:else if loadError}
          <div class="error-container">
        <div class="error-icon">!</div>
        <ErrorMessage message={loadError} type="error" />
        <Button variant="primary" on:click={fetchCompanyProjects}>다시 시도</Button>
      </div>
  {:else}
    <div class="content-grid">
      <!-- 내 프로젝트 목록 -->
      <div class="project-list-section">
        <h3>내 공고 목록</h3>
        {#if projects.length === 0}
          <EmptyState 
            title="등록된 공고가 없습니다" 
            description="새로운 프로젝트 공고를 등록해보세요." 
            actionText="새 공고 등록" 
            onAction={() => setPage('project-register')}
            icon="📋"
          />
          
        {:else}
          <ul class="project-list">
            {#each projects as project (project.id)}
              <li 
                class="project-item" 
                class:selected={selectedProject && selectedProject.id === project.id}
                on:keydown={(e) => e.key === 'Enter' && selectProject(project)}
                on:click={() => selectProject(project)}
              >
                <div class="project-name">{project.title}</div>
                <div class="project-info">
                  <span class="project-company">{project.company || '비공개'}</span>
                  <span class="project-date">{new Date(project.createdAt).toLocaleDateString()}</span>
                </div>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
      
      <!-- 지원자 목록 -->
      <div class="applicants-section">
        {#if selectedProject}
          <div class="applicants-header">
            <h3>{selectedProject.title} - 지원자 목록</h3>
            <div class="header-actions">
              <span class="applicant-count">총 {applicants.length}명</span>
              <Button variant="primary" size="sm" on:click={editProject}>공고 수정</Button>
            </div>
          </div>
          
          {#if applicants.length === 0}
            <EmptyState 
              title="지원자가 없습니다" 
              description="아직 이 공고에 지원한 지원자가 없습니다." 
              icon="👥"
            />
          {:else}
            <DataTable 
              data={formattedApplicants}
              columns={applicantColumns}
              loading={isLoading}
              striped={true}
              hover={true}
              responsive={true}
            />
          {/if}
        {:else}
          <div class="select-prompt">
            <div class="prompt-icon">👈</div>
            <div class="prompt-text">좌측에서 공고를 선택하세요</div>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .company-applicants-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    font-family: 'Noto Sans KR', sans-serif;
  }
  
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
  }
  
  .page-header h2 {
    font-size: 1.8rem;
    font-weight: 700;
    color: #333;
  }
  
  .back-button {
    background: none;
    border: none;
    color: #3273dc;
    font-size: 1rem;
    cursor: pointer;
    padding: 5px 10px;
  }
  
  .back-button:hover {
    text-decoration: underline;
  }
  
  .content-grid {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 30px;
  }
  
  .project-list-section {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    padding: 20px;
  }
  
  .project-list-section h3 {
    font-size: 1.2rem;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
  }
  
  .project-list {
    list-style: none;
    padding: 0;
    margin: 0;
    min-height: 800px;
    /* overflow-y: auto; 제거하여 스크롤바 제거 */
  }
  
  .project-item {
    padding: 15px;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .project-item:hover {
    background-color: #f8f9fa;
  }
  
  .project-item.selected {
    background-color: #e7f5ff;
    border-left: 3px solid #3273dc;
  }
  
  .project-name {
    font-weight: 500;
    margin-bottom: 5px;
  }
  
  .project-info {
    display: flex;
    justify-content: space-between;
    font-size: 0.85rem;
    color: #6c757d;
  }
  

  
  .applicants-section {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    padding: 20px;
    min-height: 500px;
  }
  
  .applicants-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
  }
  
  .applicants-header h3 {
    font-size: 1.2rem;
    margin: 0;
  }
  
  .header-actions {
    display: flex;
    align-items: center;
    gap: 15px;
  }
  
  .applicant-count {
    color: #6c757d;
    font-size: 0.9rem;
  }
  

  
  .select-prompt {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
    color: #868e96;
  }
  
  .prompt-icon {
    font-size: 3rem;
    margin-bottom: 15px;
  }
  
  .prompt-text {
    font-size: 1.2rem;
  }
  

  
  /* 반응형 스타일 */
  @media (max-width: 992px) {
    .content-grid {
      grid-template-columns: 1fr;
    }
    
    .applicants-section {
      margin-top: 20px;
    }
  }
</style> 